/**
 * Turns a duplex `transcript_delta` stream into whole transcript lines for
 * the live-call transcript bar (CombinedInfoBar -> conversationSlice
 * `transcripts`).
 *
 * The live call adds one line per finished utterance, so deltas are held and
 * committed as one line when the speaker changes, when the stream goes quiet
 * for TRANSCRIPT_IDLE_FLUSH_MS, or when the caller knows a line is over
 * (`flush`, e.g. the server says the customer has raised an item). Meanwhile
 * the line in progress is shown word by word (`interimTranscript`), so the
 * rep reads along instead of waiting for the whole utterance (owner, 2026-09-26).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import type { KbSpeaker } from '../services/kbLiveFrames';
import { addTranscript, clearInterimTranscript, setInterimTranscript } from '../store/conversationSlice';
import { toTranscriptSpeaker } from '../utils/trainingCallActions';

/** A pause this long between deltas ends the utterance. */
export const TRANSCRIPT_IDLE_FLUSH_MS = 1200;

interface OpenLine {
  speaker: KbSpeaker;
  text: string;
}

export interface TrainingTranscript {
  appendDelta: (speaker: KbSpeaker, text: string) => void;
  flush: () => void;
  /** Drops the open line without committing it. */
  discard: () => void;
}

export function useTrainingTranscript(): TrainingTranscript {
  const dispatch = useDispatch();
  const openLineRef = useRef<OpenLine | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = null;
  }, []);

  const flush = useCallback(() => {
    clearIdleTimer();
    const openLine = openLineRef.current;
    openLineRef.current = null;
    dispatch(clearInterimTranscript());
    const text = openLine?.text?.trim();
    if (!openLine || !text) return;
    dispatch(
      addTranscript({
        text,
        speaker: toTranscriptSpeaker(openLine.speaker),
        timestamp: new Date().toISOString(),
      })
    );
  }, [clearIdleTimer, dispatch]);

  const appendDelta = useCallback(
    (speaker: KbSpeaker, text: string) => {
      if (!text) return;
      if (openLineRef.current && openLineRef.current.speaker !== speaker) flush();
      openLineRef.current = { speaker, text: (openLineRef.current?.text ?? '') + text };
      dispatch(setInterimTranscript({ speaker: toTranscriptSpeaker(speaker), text: openLineRef.current.text }));
      clearIdleTimer();
      idleTimerRef.current = setTimeout(flush, TRANSCRIPT_IDLE_FLUSH_MS);
    },
    [clearIdleTimer, dispatch, flush]
  );

  const discard = useCallback(() => {
    clearIdleTimer();
    openLineRef.current = null;
    dispatch(clearInterimTranscript());
  }, [clearIdleTimer, dispatch]);

  useEffect(() => discard, [discard]);

  return useMemo(() => ({ appendDelta, flush, discard }), [appendDelta, flush, discard]);
}
